import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { trackPDFDownload } from './analyticsEvents';

// Northstar Analytics Brand Colors
const deepMidnightBlue = [0, 43, 69]; // #002B45 - Primary
const tealBlue = [31, 181, 165]; // #1FB5A5 - Accent
const polarisWhite = [249, 251, 255]; // #F9FBFF - Background
const coolGraphite = [74, 74, 74]; // #4A4A4A - Text
const arcticSky = [180, 225, 224]; // #B4E1E0 - Light Accent

// Helper function to add branded header
const addBrandedHeader = (doc, title, subtitle) => {
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  
  // Header background
  doc?.setFillColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.rect(0, 0, pageWidth, 50, 'F');
  
  // Teal Blue accent strip
  doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.rect(0, 45, pageWidth, 5, 'F');
  
  // Company name
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(24);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Northstar Analytics', pageWidth / 2, 22, { align: 'center' });
  
  // Document title
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'normal');
  doc?.text(title, pageWidth / 2, 33, { align: 'center' });
  
  // Subtitle/date
  doc?.setFontSize(9);
  doc?.setTextColor(arcticSky?.[0], arcticSky?.[1], arcticSky?.[2]);
  doc?.text(subtitle, pageWidth / 2, 42, { align: 'center' });
};

// Helper function to add section header
const addSectionHeader = (doc, text, yPosition) => {
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  doc?.text(text, 20, yPosition);
  
  // Decorative line
  doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.setLineWidth(2);
  const textWidth = doc?.getTextWidth(text);
  doc?.line(20, yPosition + 2, 20 + textWidth, yPosition + 2);
};

// Helper function to check page break
const checkPageBreak = (doc, yPosition, requiredSpace = 20) => {
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  if (yPosition + requiredSpace > pageHeight - 20) {
    doc?.addPage();
    return 20;
  }
  return yPosition;
};

// Helper function to add footer
const addFooter = (doc) => {
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  const pageCount = doc?.internal?.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    doc?.setPage(i);
    doc?.setFontSize(9);
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.text(
      `© ${new Date()?.getFullYear()} Northstar Analytics | www.northstaranalytics.eu`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    doc?.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
  }
};

// Generate Data Strategy Framework Guide
export const generateDataStrategyFrameworkPDF = () => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  let yPosition = 20;

  // Header
  addBrandedHeader(
    doc,
    'Data Strategy Framework Guide',
    `Comprehensive Guide to Building a Modern Data Strategy | ${new Date()?.toLocaleDateString()}`
  );

  yPosition = 65;

  // Introduction
  addSectionHeader(doc, 'Introduction', yPosition);
  yPosition += 12;
  
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  const introText = doc?.splitTextToSize(
    'A robust data strategy is the foundation of digital transformation and competitive advantage. This framework provides a structured approach to developing, implementing, and evolving your organization\'s data capabilities.',
    pageWidth - 40
  );
  doc?.text(introText, 20, yPosition);
  yPosition += introText?.length * 6 + 15;

  // Framework Components
  addSectionHeader(doc, '1. Strategic Vision & Objectives', yPosition);
  yPosition += 12;

  const visionItems = [
    { title: 'Business Alignment', desc: 'Ensure data initiatives support core business objectives and strategic priorities.' },
    { title: 'Value Proposition', desc: 'Define clear ROI expectations and success metrics for data investments.' },
    { title: 'Stakeholder Engagement', desc: 'Identify key stakeholders and establish governance structures.' },
    { title: 'Vision Statement', desc: 'Articulate a compelling vision for data-driven decision making.' }
  ];

  visionItems?.forEach((item) => {
    yPosition = checkPageBreak(doc, yPosition, 25);
    
    // Bullet point
    doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.circle(22, yPosition - 2, 1.5, 'F');
    
    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(11);
    doc?.text(item?.title, 27, yPosition);
    
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(10);
    const descLines = doc?.splitTextToSize(item?.desc, pageWidth - 50);
    doc?.text(descLines, 27, yPosition + 5);
    yPosition += descLines?.length * 5 + 10;
  });

  yPosition += 5;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Data Architecture
  addSectionHeader(doc, '2. Data Architecture & Infrastructure', yPosition);
  yPosition += 12;

  const architectureData = [
    ['Component', 'Description', 'Key Considerations'],
    ['Data Sources', 'Internal and external data origins', 'Quality, accessibility, integration'],
    ['Data Storage', 'Cloud/on-premise repositories', 'Scalability, security, cost'],
    ['Data Processing', 'ETL/ELT pipelines', 'Performance, reliability, automation'],
    ['Data Access', 'APIs, dashboards, tools', 'User experience, permissions, speed'],
    ['Data Security', 'Encryption, access controls', 'Compliance, privacy, monitoring']
  ];

  doc?.autoTable({
    startY: yPosition,
    head: [architectureData?.[0]],
    body: architectureData?.slice(1),
    theme: 'grid',
    headStyles: {
      fillColor: deepMidnightBlue,
      fontSize: 10,
      fontStyle: 'bold',
      textColor: [255, 255, 255]
    },
    bodyStyles: { fontSize: 9, textColor: coolGraphite },
    alternateRowStyles: { fillColor: polarisWhite },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Analytics Capabilities
  addSectionHeader(doc, '3. Analytics Capabilities', yPosition);
  yPosition += 12;

  const analyticsLevels = [
    ['Maturity Level', 'Capabilities', 'Business Impact'],
    ['Descriptive', 'What happened? Historical reporting', 'Operational visibility'],
    ['Diagnostic', 'Why did it happen? Root cause analysis', 'Problem identification'],
    ['Predictive', 'What will happen? Forecasting models', 'Proactive planning'],
    ['Prescriptive', 'What should we do? Optimization', 'Automated decision-making']
  ];

  doc?.autoTable({
    startY: yPosition,
    head: [analyticsLevels?.[0]],
    body: analyticsLevels?.slice(1),
    theme: 'striped',
    headStyles: {
      fillColor: deepMidnightBlue,
      fontSize: 10,
      fontStyle: 'bold',
      textColor: [255, 255, 255]
    },
    bodyStyles: { fontSize: 9, textColor: coolGraphite },
    alternateRowStyles: { fillColor: polarisWhite },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Governance Framework
  addSectionHeader(doc, '4. Data Governance', yPosition);
  yPosition += 12;

  const governanceText = doc?.splitTextToSize(
    'Establish clear policies, standards, and accountability for data management across the organization. Key elements include:',
    pageWidth - 40
  );
  doc?.text(governanceText, 20, yPosition);
  yPosition += governanceText?.length * 6 + 10;

  const governanceItems = [
    'Data Quality Standards: Define metrics and processes for data accuracy, completeness, and consistency',
    'Data Ownership: Assign clear accountability for data domains and assets',
    'Access Management: Implement role-based access controls and audit trails',
    'Compliance Framework: Ensure adherence to GDPR, industry regulations, and internal policies',
    'Metadata Management: Maintain comprehensive data catalogs and lineage documentation'
  ];

  governanceItems?.forEach((item) => {
    yPosition = checkPageBreak(doc, yPosition, 15);
    doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.circle(22, yPosition - 2, 1.5, 'F');
    const itemLines = doc?.splitTextToSize(item, pageWidth - 50);
    doc?.text(itemLines, 27, yPosition);
    yPosition += itemLines?.length * 5 + 8;
  });

  yPosition += 5;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Implementation Roadmap
  addSectionHeader(doc, '5. Implementation Roadmap', yPosition);
  yPosition += 12;

  const roadmapPhases = [
    ['Phase', 'Duration', 'Key Activities', 'Success Metrics'],
    ['Assessment', '4-6 weeks', 'Current state analysis, gap identification', 'Baseline metrics established'],
    ['Foundation', '3-4 months', 'Infrastructure setup, governance framework', 'Core systems operational'],
    ['Enablement', '4-6 months', 'Analytics tools, training programs', 'User adoption >70%'],
    ['Optimization', 'Ongoing', 'Advanced analytics, AI/ML integration', 'Measurable ROI achieved']
  ];

  doc?.autoTable({
    startY: yPosition,
    head: [roadmapPhases?.[0]],
    body: roadmapPhases?.slice(1),
    theme: 'grid',
    headStyles: {
      fillColor: deepMidnightBlue,
      fontSize: 9,
      fontStyle: 'bold',
      textColor: [255, 255, 255]
    },
    bodyStyles: { fontSize: 8, textColor: coolGraphite },
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 30 },
      2: { cellWidth: 65 },
      3: { cellWidth: 45 }
    },
    alternateRowStyles: { fillColor: polarisWhite },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Success Factors
  addSectionHeader(doc, '6. Critical Success Factors', yPosition);
  yPosition += 12;

  const successFactors = [
    { factor: 'Executive Sponsorship', importance: 'Secure C-level commitment and resource allocation' },
    { factor: 'Change Management', importance: 'Address cultural resistance and drive adoption' },
    { factor: 'Skill Development', importance: 'Invest in training and talent acquisition' },
    { factor: 'Incremental Value', importance: 'Deliver quick wins to maintain momentum' },
    { factor: 'Continuous Improvement', importance: 'Establish feedback loops and iterate' }
  ];

  successFactors?.forEach((item, index) => {
    yPosition = checkPageBreak(doc, yPosition, 20);
    
    // Number badge
    doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.circle(23, yPosition - 2, 3, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.text(`${index + 1}`, 23, yPosition, { align: 'center' });
    
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.setFontSize(11);
    doc?.text(item?.factor, 30, yPosition);
    
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(10);
    const importanceLines = doc?.splitTextToSize(item?.importance, pageWidth - 55);
    doc?.text(importanceLines, 30, yPosition + 5);
    yPosition += importanceLines?.length * 5 + 12;
  });

  // Add footer
  addFooter(doc);

  // Track download
  trackPDFDownload('Data Strategy Framework Guide');

  // Save PDF
  doc?.save('Northstar_Data_Strategy_Framework_Guide.pdf');
};

// Generate Consultation Preparation Template
export const generateConsultationPreparationPDF = () => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  let yPosition = 20;

  // Header
  addBrandedHeader(
    doc,
    'Consultation Preparation Template',
    `Structured Template to Maximize Your Consultation Value | ${new Date()?.toLocaleDateString()}`
  );

  yPosition = 65;

  // Introduction
  addSectionHeader(doc, 'How to Use This Template', yPosition);
  yPosition += 12;
  
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  const introText = doc?.splitTextToSize(
    'This template helps you organize your thoughts, questions, and objectives before your consultation. Complete each section to ensure a productive and focused discussion with our data strategy experts.',
    pageWidth - 40
  );
  doc?.text(introText, 20, yPosition);
  yPosition += introText?.length * 6 + 15;

  // Section 1: Organization Overview
  addSectionHeader(doc, '1. Organization Overview', yPosition);
  yPosition += 12;

  const orgQuestions = [
    'Company Name & Industry:',
    'Number of Employees:',
    'Annual Revenue Range:',
    'Primary Business Model:',
    'Key Products/Services:',
    'Target Markets/Customers:'
  ];

  orgQuestions?.forEach((question) => {
    yPosition = checkPageBreak(doc, yPosition, 20);
    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(10);
    doc?.text(question, 20, yPosition);
    
    // Answer line
    doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.setLineWidth(0.3);
    doc?.line(20, yPosition + 5, pageWidth - 20, yPosition + 5);
    yPosition += 15;
  });

  yPosition += 5;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Section 2: Current Data Landscape
  addSectionHeader(doc, '2. Current Data Landscape', yPosition);
  yPosition += 12;

  const dataQuestions = [
    { q: 'What data sources do you currently use?', lines: 3 },
    { q: 'What tools/platforms are in your tech stack?', lines: 3 },
    { q: 'How is data currently stored and accessed?', lines: 2 },
    { q: 'Who are the primary data users in your organization?', lines: 2 }
  ];

  dataQuestions?.forEach((item) => {
    yPosition = checkPageBreak(doc, yPosition, 15 + (item?.lines * 8));
    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(10);
    doc?.text(item?.q, 20, yPosition);
    yPosition += 7;
    
    // Multiple answer lines
    doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.setLineWidth(0.3);
    for (let i = 0; i < item?.lines; i++) {
      doc?.line(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 8;
    }
    yPosition += 5;
  });

  yPosition = checkPageBreak(doc, yPosition, 40);

  // Section 3: Challenges & Pain Points
  addSectionHeader(doc, '3. Challenges & Pain Points', yPosition);
  yPosition += 12;

  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(10);
  doc?.text('Identify your top 3-5 data-related challenges:', 20, yPosition);
  yPosition += 10;

  for (let i = 1; i <= 5; i++) {
    yPosition = checkPageBreak(doc, yPosition, 25);
    
    // Number badge
    doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.circle(23, yPosition - 2, 3, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.text(`${i}`, 23, yPosition, { align: 'center' });
    
    // Answer lines
    doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.setLineWidth(0.3);
    doc?.line(30, yPosition + 3, pageWidth - 20, yPosition + 3);
    doc?.line(30, yPosition + 11, pageWidth - 20, yPosition + 11);
    yPosition += 20;
  }

  yPosition += 5;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Section 4: Strategic Objectives
  addSectionHeader(doc, '4. Strategic Objectives', yPosition);
  yPosition += 12;

  const objectiveQuestions = [
    { q: 'What are your primary business goals for the next 12 months?', lines: 3 },
    { q: 'How do you envision data supporting these goals?', lines: 3 },
    { q: 'What does success look like for your data initiatives?', lines: 2 },
    { q: 'What ROI or business impact are you targeting?', lines: 2 }
  ];

  objectiveQuestions?.forEach((item) => {
    yPosition = checkPageBreak(doc, yPosition, 15 + (item?.lines * 8));
    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(10);
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.text(item?.q, 20, yPosition);
    yPosition += 7;
    
    doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.setLineWidth(0.3);
    for (let i = 0; i < item?.lines; i++) {
      doc?.line(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 8;
    }
    yPosition += 5;
  });

  yPosition = checkPageBreak(doc, yPosition, 40);

  // Section 5: Questions for Consultation
  addSectionHeader(doc, '5. Questions for Our Experts', yPosition);
  yPosition += 12;

  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(10);
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.text('Prepare your key questions to make the most of your consultation time:', 20, yPosition);
  yPosition += 10;

  for (let i = 1; i <= 6; i++) {
    yPosition = checkPageBreak(doc, yPosition, 20);
    doc?.setFont('helvetica', 'bold');
    doc?.text(`Question ${i}:`, 20, yPosition);
    
    doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.setLineWidth(0.3);
    doc?.line(20, yPosition + 5, pageWidth - 20, yPosition + 5);
    doc?.line(20, yPosition + 13, pageWidth - 20, yPosition + 13);
    yPosition += 22;
  }

  yPosition += 5;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Section 6: Budget & Timeline
  addSectionHeader(doc, '6. Budget & Timeline Considerations', yPosition);
  yPosition += 12;

  const budgetQuestions = [
    'What is your budget range for data initiatives?',
    'What is your desired timeline for implementation?',
    'Are there any critical deadlines or milestones?',
    'What internal resources can you allocate to this project?'
  ];

  budgetQuestions?.forEach((question) => {
    yPosition = checkPageBreak(doc, yPosition, 20);
    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(10);
    doc?.text(question, 20, yPosition);
    
    doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.setLineWidth(0.3);
    doc?.line(20, yPosition + 5, pageWidth - 20, yPosition + 5);
    doc?.line(20, yPosition + 13, pageWidth - 20, yPosition + 13);
    yPosition += 22;
  });

  yPosition += 5;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Section 7: Additional Notes
  addSectionHeader(doc, '7. Additional Notes & Context', yPosition);
  yPosition += 12;

  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(10);
  doc?.text('Any other information that would be helpful for our discussion:', 20, yPosition);
  yPosition += 10;

  doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.setLineWidth(0.3);
  for (let i = 0; i < 8; i++) {
    yPosition = checkPageBreak(doc, yPosition, 10);
    doc?.line(20, yPosition, pageWidth - 20, yPosition);
    yPosition += 8;
  }

  // Add footer
  addFooter(doc);

  // Track download
  trackPDFDownload('Consultation Preparation Template');

  // Save PDF
  doc?.save('Northstar_Consultation_Preparation_Template.pdf');
};

// Generate Governance Framework Template
export const generateGovernanceFrameworkPDF = () => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  let yPosition = 20;

  // Header
  addBrandedHeader(
    doc,
    'Data Governance Framework Template',
    `Establish Robust Data Governance Policies & Procedures | ${new Date()?.toLocaleDateString()}`
  );

  yPosition = 65;

  // Introduction
  addSectionHeader(doc, 'Executive Summary', yPosition);
  yPosition += 12;
  
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  const introText = doc?.splitTextToSize(
    'This governance framework template provides a comprehensive structure for establishing data governance policies, roles, and procedures in your organization. Adapt each section to your specific organizational context and regulatory requirements.',
    pageWidth - 40
  );
  doc?.text(introText, 20, yPosition);
  yPosition += introText?.length * 6 + 15;

  // Governance Principles
  addSectionHeader(doc, '1. Governance Principles', yPosition);
  yPosition += 12;

  const principles = [
    { title: 'Accountability', desc: 'Clear ownership and responsibility for data assets across the organization' },
    { title: 'Transparency', desc: 'Open communication about data policies, usage, and decision-making processes' },
    { title: 'Integrity', desc: 'Maintain accuracy, consistency, and trustworthiness of data throughout its lifecycle' },
    { title: 'Protection', desc: 'Safeguard sensitive data through appropriate security and privacy controls' },
    { title: 'Compliance', desc: 'Adhere to all relevant regulations, standards, and industry best practices' },
    { title: 'Value Creation', desc: 'Maximize business value while managing data-related risks effectively' }
  ];

  principles?.forEach((item) => {
    yPosition = checkPageBreak(doc, yPosition, 20);
    
    doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.circle(22, yPosition - 2, 1.5, 'F');
    
    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(11);
    doc?.text(item?.title, 27, yPosition);
    
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(10);
    const descLines = doc?.splitTextToSize(item?.desc, pageWidth - 50);
    doc?.text(descLines, 27, yPosition + 5);
    yPosition += descLines?.length * 5 + 10;
  });

  yPosition += 5;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Governance Structure
  addSectionHeader(doc, '2. Governance Structure & Roles', yPosition);
  yPosition += 12;

  const rolesData = [
    ['Role', 'Responsibilities', 'Authority Level'],
    ['Data Governance Council', 'Strategic oversight, policy approval', 'Executive'],
    ['Chief Data Officer (CDO)', 'Program leadership, strategy execution', 'Executive'],
    ['Data Stewards', 'Domain-specific data quality, standards', 'Operational'],
    ['Data Owners', 'Business accountability for data assets', 'Tactical'],
    ['Data Custodians', 'Technical implementation, maintenance', 'Technical']
  ];

  doc?.autoTable({
    startY: yPosition,
    head: [rolesData?.[0]],
    body: rolesData?.slice(1),
    theme: 'grid',
    headStyles: {
      fillColor: deepMidnightBlue,
      fontSize: 10,
      fontStyle: 'bold',
      textColor: [255, 255, 255]
    },
    bodyStyles: { fontSize: 9, textColor: coolGraphite },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 70 },
      2: { cellWidth: 40 }
    },
    alternateRowStyles: { fillColor: polarisWhite },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Data Quality Standards
  addSectionHeader(doc, '3. Data Quality Standards', yPosition);
  yPosition += 12;

  const qualityDimensions = [
    ['Dimension', 'Definition', 'Measurement Approach'],
    ['Accuracy', 'Data correctly represents reality', 'Error rate, validation checks'],
    ['Completeness', 'All required data is present', 'Null value percentage, coverage'],
    ['Consistency', 'Data is uniform across systems', 'Cross-system reconciliation'],
    ['Timeliness', 'Data is current and up-to-date', 'Refresh frequency, latency'],
    ['Validity', 'Data conforms to defined formats', 'Schema compliance, range checks'],
    ['Uniqueness', 'No unintended duplicates exist', 'Duplicate detection rate']
  ];

  doc?.autoTable({
    startY: yPosition,
    head: [qualityDimensions?.[0]],
    body: qualityDimensions?.slice(1),
    theme: 'striped',
    headStyles: {
      fillColor: deepMidnightBlue,
      fontSize: 9,
      fontStyle: 'bold',
      textColor: [255, 255, 255]
    },
    bodyStyles: { fontSize: 8, textColor: coolGraphite },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 65 },
      2: { cellWidth: 60 }
    },
    alternateRowStyles: { fillColor: polarisWhite },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Access Control Framework
  addSectionHeader(doc, '4. Access Control & Security', yPosition);
  yPosition += 12;

  const accessLevels = [
    ['Access Level', 'Description', 'Approval Required'],
    ['Public', 'Non-sensitive, publicly available data', 'Data Owner'],
    ['Internal', 'General business data for employees', 'Manager'],
    ['Confidential', 'Sensitive business information', 'Data Owner + Security'],
    ['Restricted', 'Highly sensitive, regulated data', 'CDO + Legal + Security'],
    ['Critical', 'Mission-critical, high-risk data', 'Executive Committee']
  ];

  doc?.autoTable({
    startY: yPosition,
    head: [accessLevels?.[0]],
    body: accessLevels?.slice(1),
    theme: 'grid',
    headStyles: {
      fillColor: deepMidnightBlue,
      fontSize: 10,
      fontStyle: 'bold',
      textColor: [255, 255, 255]
    },
    bodyStyles: { fontSize: 9, textColor: coolGraphite },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 80 },
      2: { cellWidth: 45 }
    },
    alternateRowStyles: { fillColor: polarisWhite },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Compliance Requirements
  addSectionHeader(doc, '5. Compliance & Regulatory Framework', yPosition);
  yPosition += 12;

  const complianceItems = [
    { reg: 'GDPR (General Data Protection Regulation)', req: 'Data subject rights, consent management, breach notification, data minimization' },
    { reg: 'Data Protection Act 2018 (Ireland)', req: 'National implementation of GDPR, additional Irish-specific requirements' },
    { reg: 'Industry-Specific Regulations', req: 'Sector-specific compliance (e.g., financial services, healthcare)' }
  ];

  complianceItems?.forEach((item) => {
    yPosition = checkPageBreak(doc, yPosition, 25);
    
    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(10);
    doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.text(item?.reg, 20, yPosition);
    
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(9);
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    const reqLines = doc?.splitTextToSize(item?.req, pageWidth - 45);
    doc?.text(reqLines, 25, yPosition + 5);
    yPosition += reqLines?.length * 5 + 12;
  });

  yPosition += 5;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Data Lifecycle Management
  addSectionHeader(doc, '6. Data Lifecycle Management', yPosition);
  yPosition += 12;

  const lifecyclePhases = [
    ['Phase', 'Activities', 'Governance Controls'],
    ['Creation/Acquisition', 'Data sourcing, collection', 'Quality validation, consent capture'],
    ['Storage', 'Repository management', 'Access controls, encryption'],
    ['Usage', 'Analysis, reporting, sharing', 'Purpose limitation, audit logging'],
    ['Archival', 'Long-term retention', 'Retention policies, compliance'],
    ['Disposal', 'Secure deletion', 'Destruction certification, audit trail']
  ];

  doc?.autoTable({
    startY: yPosition,
    head: [lifecyclePhases?.[0]],
    body: lifecyclePhases?.slice(1),
    theme: 'striped',
    headStyles: {
      fillColor: deepMidnightBlue,
      fontSize: 10,
      fontStyle: 'bold',
      textColor: [255, 255, 255]
    },
    bodyStyles: { fontSize: 9, textColor: coolGraphite },
    columnStyles: {
      0: { cellWidth: 45 },
      1: { cellWidth: 55 },
      2: { cellWidth: 60 }
    },
    alternateRowStyles: { fillColor: polarisWhite },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Monitoring & Reporting
  addSectionHeader(doc, '7. Monitoring & Reporting', yPosition);
  yPosition += 12;

  const monitoringText = doc?.splitTextToSize(
    'Establish regular monitoring and reporting mechanisms to ensure ongoing governance effectiveness:',
    pageWidth - 40
  );
  doc?.text(monitoringText, 20, yPosition);
  yPosition += monitoringText?.length * 6 + 10;

  const monitoringItems = [
    'Data Quality Dashboards: Real-time monitoring of quality metrics across all data domains',
    'Compliance Audits: Quarterly reviews of regulatory compliance and policy adherence',
    'Access Reviews: Monthly certification of user access rights and permissions',
    'Incident Reporting: Immediate escalation and documentation of data breaches or quality issues',
    'Governance Scorecard: Executive reporting on governance program maturity and effectiveness'
  ];

  monitoringItems?.forEach((item, index) => {
    yPosition = checkPageBreak(doc, yPosition, 18);
    
    doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.circle(23, yPosition - 2, 3, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.text(`${index + 1}`, 23, yPosition, { align: 'center' });
    
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(10);
    const itemLines = doc?.splitTextToSize(item, pageWidth - 55);
    doc?.text(itemLines, 30, yPosition);
    yPosition += itemLines?.length * 5 + 10;
  });

  yPosition += 5;
  yPosition = checkPageBreak(doc, yPosition, 40);

  // Implementation Checklist
  addSectionHeader(doc, '8. Implementation Checklist', yPosition);
  yPosition += 12;

  const checklistItems = [
    'Establish Data Governance Council and assign roles',
    'Document and communicate governance policies',
    'Implement data classification and tagging system',
    'Deploy access control and security measures',
    'Create data quality monitoring dashboards',
    'Conduct governance training for all stakeholders',
    'Establish incident response procedures',
    'Schedule regular governance reviews and audits',
    'Define and track governance KPIs',
    'Create continuous improvement feedback loop'
  ];

  checklistItems?.forEach((item) => {
    yPosition = checkPageBreak(doc, yPosition, 12);
    
    // Checkbox
    doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.setLineWidth(0.5);
    doc?.rect(20, yPosition - 4, 4, 4, 'S');
    
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(10);
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    const itemLines = doc?.splitTextToSize(item, pageWidth - 50);
    doc?.text(itemLines, 27, yPosition);
    yPosition += itemLines?.length * 5 + 8;
  });

  // Add footer
  addFooter(doc);

  // Track download
  trackPDFDownload('Governance Framework Template');

  // Save PDF
  doc?.save('Northstar_Governance_Framework_Template.pdf');
};

// Generate Key Stakeholders Identification Guide
export const generateKeyStakeholdersPDF = () => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  let yPosition = 20;

  // Header
  addBrandedHeader(
    doc,
    'Key Stakeholders Identification Guide',
    `Strategic Stakeholder Mapping for Data Initiatives | ${new Date()?.toLocaleDateString()}`
  );

  yPosition = 65;

  // Introduction
  doc?.setFontSize(11);
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.setFont('helvetica', 'normal');
  const introText = 'Identifying the right stakeholders is crucial for the success of your data strategy consultation. This guide helps you map key decision-makers, influencers, and end-users who should be involved in your data transformation journey.';
  const splitIntro = doc?.splitTextToSize(introText, pageWidth - 40);
  doc?.text(splitIntro, 20, yPosition);
  yPosition += splitIntro?.length * 5 + 10;

  // Executive Level Stakeholders
  yPosition = checkPageBreak(doc, yPosition, 40);
  addSectionHeader(doc, '1. Executive Level Stakeholders', yPosition);
  yPosition += 12;

  const executiveStakeholders = [
    {
      role: 'CEO/Managing Director',
      responsibility: 'Overall vision and budget approval',
      whyImportant: 'Sets strategic direction and ensures alignment with business objectives',
      questions: ['What are your top 3 business priorities?', 'What ROI expectations do you have?']
    },
    {
      role: 'CTO/Head of Technology',
      responsibility: 'Technical feasibility and integration',
      whyImportant: 'Ensures technical viability and system compatibility',
      questions: ['What is your current tech stack?', 'What are your integration concerns?']
    },
    {
      role: 'CFO',
      responsibility: 'ROI expectations and budget management',
      whyImportant: 'Controls financial resources and measures business impact',
      questions: ['What is the approved budget range?', 'How do you measure success financially?']
    },
    {
      role: 'Head of Operations',
      responsibility: 'Process impact and implementation',
      whyImportant: 'Manages operational changes and workflow integration',
      questions: ['What operational pain points exist?', 'How will this impact daily operations?']
    }
  ];

  executiveStakeholders?.forEach((stakeholder, index) => {
    yPosition = checkPageBreak(doc, yPosition, 35);
    
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(`${stakeholder?.role}`, 25, yPosition);
    yPosition += 6;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.text(`Responsibility: ${stakeholder?.responsibility}`, 30, yPosition);
    yPosition += 5;
    doc?.text(`Why Important: ${stakeholder?.whyImportant}`, 30, yPosition);
    yPosition += 5;
    
    doc?.setFont('helvetica', 'italic');
    doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.text('Key Questions:', 30, yPosition);
    yPosition += 5;
    
    stakeholder?.questions?.forEach((question) => {
      const questionText = `• ${question}`;
      const splitQuestion = doc?.splitTextToSize(questionText, pageWidth - 75);
      doc?.text(splitQuestion, 35, yPosition);
      yPosition += splitQuestion?.length * 4;
    });
    
    yPosition += 5;
  });

  // Departmental Leaders
  yPosition = checkPageBreak(doc, yPosition, 40);
  addSectionHeader(doc, '2. Departmental Leaders', yPosition);
  yPosition += 12;

  const departmentalLeaders = [
    {
      role: 'Marketing Director',
      responsibility: 'Customer data and analytics needs',
      impact: 'Customer insights, campaign optimization, attribution modeling'
    },
    {
      role: 'Sales Manager',
      responsibility: 'CRM and performance tracking requirements',
      impact: 'Pipeline visibility, forecasting accuracy, conversion optimization'
    },
    {
      role: 'HR Director',
      responsibility: 'Employee data and compliance considerations',
      impact: 'Workforce analytics, talent retention, compliance reporting'
    },
    {
      role: 'Compliance Officer',
      responsibility: 'Regulatory and governance oversight',
      impact: 'Data privacy, regulatory compliance, audit trails'
    }
  ];

  departmentalLeaders?.forEach((leader) => {
    yPosition = checkPageBreak(doc, yPosition, 20);
    
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(`• ${leader?.role}`, 25, yPosition);
    yPosition += 5;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.text(`  Responsibility: ${leader?.responsibility}`, 25, yPosition);
    yPosition += 7;
    
    doc?.text(`  Impact Areas: ${leader?.impact}`, 25, yPosition);
    yPosition += 7;
  });

  // Technical Team
  yPosition = checkPageBreak(doc, yPosition, 40);
  addSectionHeader(doc, '3. Technical Team', yPosition);
  yPosition += 12;

  const technicalTeam = [
    { role: 'IT Manager', focus: 'Infrastructure and security requirements' },
    { role: 'Data Analyst/Scientist', focus: 'Current capabilities and skill gaps' },
    { role: 'Database Administrator', focus: 'Data architecture and access' },
    { role: 'Security Officer', focus: 'Data protection and privacy concerns' }
  ];

  technicalTeam?.forEach((member) => {
    yPosition = checkPageBreak(doc, yPosition, 10);
    
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(`• ${member?.role}`, 25, yPosition);
    yPosition += 5;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.text(`  Focus: ${member?.focus}`, 25, yPosition);
    yPosition += 7;
  });

  // End Users
  yPosition = checkPageBreak(doc, yPosition, 40);
  addSectionHeader(doc, '4. End Users & Decision Makers', yPosition);
  yPosition += 12;

  const endUsers = [
    { role: 'Department Managers', need: 'Daily operational needs and reporting requirements' },
    { role: 'Report Recipients', need: 'Dashboard and analytics consumption patterns' },
    { role: 'Decision Makers', need: 'Strategic insights and actionable intelligence' }
  ];

  endUsers?.forEach((user) => {
    yPosition = checkPageBreak(doc, yPosition, 10);
    
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(`• ${user?.role}`, 25, yPosition);
    yPosition += 5;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    const needText = `  Needs: ${user?.need}`;
    const splitNeed = doc?.splitTextToSize(needText, pageWidth - 50);
    doc?.text(splitNeed, 25, yPosition);
    yPosition += splitNeed?.length * 4 + 5;
  });

  // Stakeholder Mapping Template
  yPosition = checkPageBreak(doc, yPosition, 60);
  addSectionHeader(doc, 'Stakeholder Mapping Template', yPosition);
  yPosition += 12;

  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.text('Use this template to document your stakeholders:', 20, yPosition);
  yPosition += 10;

  // Create table for stakeholder mapping
  doc?.autoTable({
    startY: yPosition,
    head: [['Stakeholder Name', 'Role', 'Department', 'Influence Level', 'Key Concerns']],
    body: [
      ['', '', '', 'High/Medium/Low', ''],
      ['', '', '', 'High/Medium/Low', ''],
      ['', '', '', 'High/Medium/Low', ''],
      ['', '', '', 'High/Medium/Low', ''],
      ['', '', '', 'High/Medium/Low', '']
    ],
    theme: 'grid',
    headStyles: {
      fillColor: deepMidnightBlue,
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 8,
      minCellHeight: 12
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;

  // Preparation Tips
  yPosition = checkPageBreak(doc, yPosition, 50);
  addSectionHeader(doc, 'Preparation Tips', yPosition);
  yPosition += 12;

  const tips = [
    'Schedule brief pre-consultation interviews with key stakeholders to understand their priorities',
    'Map each stakeholder to specific data needs and expected outcomes',
    'Identify potential champions who can drive adoption and change management',
    'Document current pain points and frustrations from each stakeholder perspective',
    'Clarify decision-making authority and approval processes',
    'Consider including stakeholders in the consultation session for alignment'
  ];

  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);

  tips?.forEach((tip, index) => {
    yPosition = checkPageBreak(doc, yPosition, 15);
    
    doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.circle(23, yPosition - 2, 1.5, 'F');
    
    const tipText = `${index + 1}. ${tip}`;
    const splitTip = doc?.splitTextToSize(tipText, pageWidth - 50);
    doc?.text(splitTip, 28, yPosition);
    yPosition += splitTip?.length * 5 + 3;
  });

  // Next Steps
  yPosition = checkPageBreak(doc, yPosition, 40);
  doc?.setFillColor(arcticSky?.[0], arcticSky?.[1], arcticSky?.[2]);
  doc?.roundedRect(20, yPosition, pageWidth - 40, 35, 3, 3, 'F');
  
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.text('Next Steps', 25, yPosition + 8);
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  const nextStepsText = 'Complete your stakeholder mapping and share this list with your Northstar Analytics consultant at least 48 hours before your scheduled consultation. This allows us to tailor our discussion to address each stakeholder\'s specific concerns and priorities.';
  const splitNextSteps = doc?.splitTextToSize(nextStepsText, pageWidth - 50);
  doc?.text(splitNextSteps, 25, yPosition + 16);

  // Add footer
  addFooter(doc);

  // Track download
  trackPDFDownload('Key Stakeholders Identification Guide');

  // Save PDF
  doc?.save('Northstar_Key_Stakeholders_Guide.pdf');
};

// Generate Success Metrics Definition Guide
export const generateSuccessMetricsPDF = () => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  let yPosition = 20;

  // Header
  addBrandedHeader(
    doc,
    'Success Metrics Definition Guide',
    `Defining Measurable Outcomes for Data Strategy Initiatives | ${new Date()?.toLocaleDateString()}`
  );

  yPosition = 65;

  // Introduction
  doc?.setFontSize(11);
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.setFont('helvetica', 'normal');
  const introText = 'Success metrics provide clear, measurable indicators of whether your data strategy initiative is delivering value. This guide helps you define meaningful KPIs across business impact, operational efficiency, technical performance, and strategic outcomes.';
  const splitIntro = doc?.splitTextToSize(introText, pageWidth - 40);
  doc?.text(splitIntro, 20, yPosition);
  yPosition += splitIntro?.length * 5 + 10;

  // Business Impact Metrics
  yPosition = checkPageBreak(doc, yPosition, 40);
  addSectionHeader(doc, '1. Business Impact Metrics', yPosition);
  yPosition += 12;

  const businessMetrics = [
    {
      metric: 'Revenue Growth',
      description: 'Percentage increase in sales or profitability attributed to data-driven decisions',
      measurement: 'Track revenue before and after implementation, isolate data-driven initiatives',
      target: 'Good: 5-10% | Better: 10-20% | Best: 20%+',
      example: 'E-commerce company increased revenue by 15% through personalized recommendations'
    },
    {
      metric: 'Cost Reduction',
      description: 'Operational efficiency savings from data optimization and automation',
      measurement: 'Calculate cost savings in labor, resources, and waste reduction',
      target: 'Good: 10-15% | Better: 15-25% | Best: 25%+',
      example: 'Manufacturing firm reduced inventory costs by 22% with predictive analytics'
    },
    {
      metric: 'Decision Speed',
      description: 'Time reduction in making strategic and operational decisions',
      measurement: 'Measure average time from question to actionable insight',
      target: 'Good: 30% faster | Better: 50% faster | Best: 70%+ faster',
      example: 'Executive team reduced strategic decision time from weeks to days'
    },
    {
      metric: 'Market Share Growth',
      description: 'Competitive advantage gained through data-driven insights',
      measurement: 'Track market position changes and competitive wins',
      target: 'Good: 2-5% | Better: 5-10% | Best: 10%+',
      example: 'Retail chain gained 8% market share with customer behavior analytics'
    }
  ];

  businessMetrics?.forEach((metric) => {
    yPosition = checkPageBreak(doc, yPosition, 45);
    
    doc?.setFontSize(11);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(metric?.metric, 25, yPosition);
    yPosition += 6;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    
    const descText = `Description: ${metric?.description}`;
    const splitDesc = doc?.splitTextToSize(descText, pageWidth - 50);
    doc?.text(splitDesc, 30, yPosition);
    yPosition += splitDesc?.length * 4 + 3;
    
    const measureText = `How to Measure: ${metric?.measurement}`;
    const splitMeasure = doc?.splitTextToSize(measureText, pageWidth - 50);
    doc?.text(splitMeasure, 30, yPosition);
    yPosition += splitMeasure?.length * 4 + 3;
    
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.text(`Target: ${metric?.target}`, 30, yPosition);
    yPosition += 5;
    
    doc?.setFont('helvetica', 'italic');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    const exampleText = `Example: ${metric?.example}`;
    const splitExample = doc?.splitTextToSize(exampleText, pageWidth - 50);
    doc?.text(splitExample, 30, yPosition);
    yPosition += splitExample?.length * 4 + 8;
  });

  // Operational Metrics
  yPosition = checkPageBreak(doc, yPosition, 40);
  addSectionHeader(doc, '2. Operational Metrics', yPosition);
  yPosition += 12;

  const operationalMetrics = [
    {
      metric: 'Data Quality Score',
      measurement: 'Accuracy, completeness, consistency, timeliness',
      target: 'Good: 85% | Better: 92% | Best: 98%+'
    },
    {
      metric: 'Report Generation Time',
      measurement: 'Time from request to delivery',
      target: 'Good: Hours | Better: Minutes | Best: Real-time'
    },
    {
      metric: 'Data Accessibility',
      measurement: 'Percentage of stakeholders with self-service access',
      target: 'Good: 40% | Better: 65% | Best: 85%+'
    },
    {
      metric: 'Process Automation',
      measurement: 'Manual tasks eliminated or automated',
      target: 'Good: 30% | Better: 50% | Best: 70%+'
    }
  ];

  operationalMetrics?.forEach((metric) => {
    yPosition = checkPageBreak(doc, yPosition, 18);
    
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(`• ${metric?.metric}`, 25, yPosition);
    yPosition += 5;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.text(`  Measurement: ${metric?.measurement}`, 30, yPosition);
    yPosition += 5;
    
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.text(`  Target: ${metric?.target}`, 30, yPosition);
    yPosition += 8;
  });

  // Technical Metrics
  yPosition = checkPageBreak(doc, yPosition, 40);
  addSectionHeader(doc, '3. Technical Metrics', yPosition);
  yPosition += 12;

  const technicalMetrics = [
    {
      metric: 'Data Integration Coverage',
      measurement: 'Number of sources connected and synchronized',
      target: 'Good: 60% | Better: 80% | Best: 95%+'
    },
    {
      metric: 'System Performance',
      measurement: 'Query response times and system uptime',
      target: 'Good: <5s, 95% uptime | Better: <2s, 99% | Best: <1s, 99.9%'
    },
    {
      metric: 'User Adoption Rate',
      measurement: 'Percentage of intended users actively engaged',
      target: 'Good: 50% | Better: 70% | Best: 85%+'
    },
    {
      metric: 'Compliance Score',
      measurement: 'Regulatory requirements met (GDPR, CCPA, etc.)',
      target: 'Good: 90% | Better: 98% | Best: 100%'
    }
  ];

  technicalMetrics?.forEach((metric) => {
    yPosition = checkPageBreak(doc, yPosition, 18);
    
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(`• ${metric?.metric}`, 25, yPosition);
    yPosition += 5;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.text(`  Measurement: ${metric?.measurement}`, 30, yPosition);
    yPosition += 5;
    
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    const targetText = `  Target: ${metric?.target}`;
    const splitTarget = doc?.splitTextToSize(targetText, pageWidth - 60);
    doc?.text(splitTarget, 30, yPosition);
    yPosition += splitTarget?.length * 4 + 5;
  });

  // Strategic Metrics
  yPosition = checkPageBreak(doc, yPosition, 40);
  addSectionHeader(doc, '4. Strategic Metrics', yPosition);
  yPosition += 12;

  const strategicMetrics = [
    {
      metric: 'Insight Generation Rate',
      measurement: 'Number of actionable insights delivered monthly',
      target: 'Good: 5-10 | Better: 10-20 | Best: 20+'
    },
    {
      metric: 'Predictive Accuracy',
      measurement: 'Forecasting precision improvement over baseline',
      target: 'Good: 10% improvement | Better: 25% | Best: 40%+'
    },
    {
      metric: 'Risk Mitigation',
      measurement: 'Early warning indicators established and monitored',
      target: 'Good: 3-5 indicators | Better: 5-10 | Best: 10+'
    },
    {
      metric: 'Innovation Enablement',
      measurement: 'New opportunities or products identified through data',
      target: 'Good: 2-3 annually | Better: 4-6 | Best: 7+'
    }
  ];

  strategicMetrics?.forEach((metric) => {
    yPosition = checkPageBreak(doc, yPosition, 18);
    
    doc?.setFontSize(10);
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.text(`• ${metric?.metric}`, 25, yPosition);
    yPosition += 5;
    
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.text(`  Measurement: ${metric?.measurement}`, 30, yPosition);
    yPosition += 5;
    
    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.text(`  Target: ${metric?.target}`, 30, yPosition);
    yPosition += 8;
  });

  // Metrics Definition Template
  yPosition = checkPageBreak(doc, yPosition, 60);
  addSectionHeader(doc, 'Your Success Metrics Template', yPosition);
  yPosition += 12;

  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.text('Define your top 5 success metrics:', 20, yPosition);
  yPosition += 10;

  // Create table for metrics definition
  doc?.autoTable({
    startY: yPosition,
    head: [['Metric Name', 'Current State', 'Target State', 'Timeline', 'Owner']],
    body: [
      ['', '', '', '3/6/12 months', ''],
      ['', '', '', '3/6/12 months', ''],
      ['', '', '', '3/6/12 months', ''],
      ['', '', '', '3/6/12 months', ''],
      ['', '', '', '3/6/12 months', '']
    ],
    theme: 'grid',
    headStyles: {
      fillColor: deepMidnightBlue,
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 8,
      minCellHeight: 12
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;

  // Best Practices
  yPosition = checkPageBreak(doc, yPosition, 50);
  addSectionHeader(doc, 'Best Practices for Defining Success Metrics', yPosition);
  yPosition += 12;

  const bestPractices = [
    'Make metrics SMART: Specific, Measurable, Achievable, Relevant, Time-bound',
    'Balance leading indicators (predictive) with lagging indicators (historical)',
    'Align metrics with overall business objectives and stakeholder priorities',
    'Set realistic baseline measurements before implementation begins',
    'Define clear ownership and accountability for each metric',
    'Plan for regular review cycles (monthly/quarterly) to track progress',
    'Include both quantitative (numbers) and qualitative (satisfaction) measures',
    'Consider short-term wins (quick wins) and long-term strategic goals'
  ];

  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);

  bestPractices?.forEach((practice, index) => {
    yPosition = checkPageBreak(doc, yPosition, 15);
    
    doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.circle(23, yPosition - 2, 1.5, 'F');
    
    const practiceText = `${index + 1}. ${practice}`;
    const splitPractice = doc?.splitTextToSize(practiceText, pageWidth - 50);
    doc?.text(splitPractice, 28, yPosition);
    yPosition += splitPractice?.length * 5 + 3;
  });

  // Common Pitfalls to Avoid
  yPosition = checkPageBreak(doc, yPosition, 40);
  addSectionHeader(doc, 'Common Pitfalls to Avoid', yPosition);
  yPosition += 12;

  const pitfalls = [
    'Vanity metrics: Tracking numbers that look good but don\'t drive decisions',
    'Too many metrics: Focus on 5-7 key metrics rather than 20+ indicators',
    'Unrealistic targets: Setting goals that demotivate teams or aren\'t achievable',
    'Lack of baseline: Not measuring current state before implementation',
    'No ownership: Metrics without clear accountability rarely improve'
  ];

  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);

  pitfalls?.forEach((pitfall) => {
    yPosition = checkPageBreak(doc, yPosition, 12);
    
    doc?.setTextColor(200, 50, 50);
    doc?.text('✗', 23, yPosition);
    
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    const pitfallText = pitfall;
    const splitPitfall = doc?.splitTextToSize(pitfallText, pageWidth - 50);
    doc?.text(splitPitfall, 28, yPosition);
    yPosition += splitPitfall?.length * 5 + 3;
  });

  // Next Steps
  yPosition = checkPageBreak(doc, yPosition, 40);
  doc?.setFillColor(arcticSky?.[0], arcticSky?.[1], arcticSky?.[2]);
  doc?.roundedRect(20, yPosition, pageWidth - 40, 40, 3, 3, 'F');
  
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.text('Next Steps', 25, yPosition + 8);
  
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  const nextStepsText = 'Complete your success metrics template and prioritize your top 5 metrics. Share this with your Northstar Analytics consultant before your session. During the consultation, we\'ll refine these metrics, establish baseline measurements, and create a tracking framework to monitor progress throughout your data transformation journey.';
  const splitNextSteps = doc?.splitTextToSize(nextStepsText, pageWidth - 50);
  doc?.text(splitNextSteps, 25, yPosition + 16);

  // Add footer
  addFooter(doc);

  // Track download
  trackPDFDownload('Success Metrics Definition Guide');

  // Save PDF
  doc?.save('Northstar_Success_Metrics_Guide.pdf');
};